/**
 * useStreamAllDraft — hook que consume el SSE stream del orchestrator
 * Generate-all (Sprint AI-1.g — Sub-fase 5.4) y mantiene el estado UI
 * de progreso multi-section.
 *
 * Diferencia clave vs `useStreamDraft` (que es por sección):
 *   - Aquí trackeamos progreso a nivel de SECCIÓN dentro del artifact
 *     completo (succeeded / failed / skipped con detalles por item).
 *   - El stream es largo (5-15 min para artifacts grandes); por eso el
 *     `start()` setea `phase = 'streaming'` y queda corriendo hasta
 *     `orchestrator_done` o `error`.
 *   - El backend persiste cada sección al terminarla (auto-save) — el
 *     UI debe invalidar las queries cache al final para que el editor
 *     muestre el contenido fresco.
 *
 * Estados expuestos:
 *   - `phase`: 'idle' | 'streaming' | 'done' | 'error'
 *   - `currentSection`: { index, total, section_id, instance_key, ... } | null
 *   - `sections`: Map<key, SectionProgress>  — tracking individual
 *   - `structurePhase`: estado de la fase de structure (si se activó)
 *   - `summary`: payload del `orchestrator_done` cuando termina
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  type DraftAllRequestBody,
  type DraftAllStreamEvent,
  type WaitingPayload,
  streamAllSectionsDraft,
} from '../api/agents'
import { ApiError } from '../api/client'
import { useAccessToken } from '../auth/useAccessToken'
import { aiWaitingFrom } from '../lib/aiWaiting'

/** Estado individual de una sección durante el run. La key es
 * `<section_id>::<instance_key>` para distinguir instancias del mismo
 * repeatable (instance_key vacío para secciones simples). */
export interface SectionProgress {
  sectionId: string
  instanceKey: string
  status: 'pending' | 'running' | 'done' | 'error' | 'skipped'
  /** Tipo de sección del template (rich_text, bullet_list, ...). Se
   * llena al recibir `section_start`. */
  sectionType?: string
  /** Razón del skip (solo si status='skipped'). */
  skipReason?: string
  /** Cuando done. */
  draftChars?: number
  tokensInput?: number
  tokensOutput?: number
  latencyMs?: number
  /** Cuando error. */
  errorCode?: string
  errorMessage?: string
}

/** Snapshot del estado durante streaming — qué sección está corriendo. */
export interface CurrentSectionInfo {
  index: number
  total: number
  sectionId: string
  instanceKey: string
  sectionType: string
}

/** Estado de la fase opcional de structure. */
export interface StructurePhaseState {
  status: 'pending' | 'running' | 'done' | 'failed'
  instancesAdded?: Record<string, number>
  rationale?: string
  errorCode?: string
  errorMessage?: string
}

/** Métricas + counts agregados que llegan en `orchestrator_done`. */
export interface OrchestratorSummary {
  total: number
  succeeded: number
  failed: number
  skippedExisting: number
  skippedNonEditable: number
  aggregateInputTokens: number
  aggregateOutputTokens: number
  aggregateTotalTokens: number
  aggregateLatencyMs: number
}

export type DraftAllPhase = 'idle' | 'streaming' | 'done' | 'error'

export interface UseStreamAllDraftResult {
  phase: DraftAllPhase
  currentSection: CurrentSectionInfo | null
  /** Map ordenado en orden de inserción: cada `section_start` agrega/
   * actualiza una entrada. */
  sections: Map<string, SectionProgress>
  structurePhase: StructurePhaseState | null
  /** Topología — total de secciones a procesar (post-filter). Se llena
   * al recibir `topology_built`. */
  totalSectionsToProcess: number
  /** Warnings emitidos por el topological sort (ciclos detectados). */
  topoWarnings: string[]
  summary: OrchestratorSummary | null
  /** Espera del modelo en curso (latido `waiting`); null si hubo progreso
   * desde el último latido o el stream no está corriendo. */
  waiting: WaitingPayload | null
  error: { code?: string; message: string } | null
  start: (
    options?: DraftAllRequestBody & { versionId?: string | null },
  ) => void
  abort: () => void
  reset: () => void
}

function makeKey(sectionId: string, instanceKey: string): string {
  return `${sectionId}::${instanceKey}`
}

export function useStreamAllDraft(
  artifactId: string,
): UseStreamAllDraftResult {
  const { accessToken } = useAccessToken()
  const [phase, setPhase] = useState<DraftAllPhase>('idle')
  const [currentSection, setCurrentSection] =
    useState<CurrentSectionInfo | null>(null)
  const [sections, setSections] = useState<Map<string, SectionProgress>>(
    () => new Map(),
  )
  const [structurePhase, setStructurePhase] =
    useState<StructurePhaseState | null>(null)
  const [totalSectionsToProcess, setTotalSectionsToProcess] = useState(0)
  const [topoWarnings, setTopoWarnings] = useState<string[]>([])
  const [summary, setSummary] = useState<OrchestratorSummary | null>(null)
  const [waiting, setWaiting] = useState<WaitingPayload | null>(null)
  const [error, setError] = useState<UseStreamAllDraftResult['error']>(null)

  const abortRef = useRef<AbortController | null>(null)

  const reset = useCallback(() => {
    setPhase('idle')
    setCurrentSection(null)
    setSections(new Map())
    setStructurePhase(null)
    setTotalSectionsToProcess(0)
    setTopoWarnings([])
    setSummary(null)
    setWaiting(null)
    setError(null)
  }, [])

  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
  }, [])

  const start = useCallback(
    (options?: DraftAllRequestBody & { versionId?: string | null }) => {
      if (!accessToken) {
        setError({ message: 'no_access_token' })
        setPhase('error')
        return
      }
      // Reset previo (relanzar tras done o error).
      setPhase('streaming')
      setCurrentSection(null)
      setSections(new Map())
      setStructurePhase(
        options?.create_structure ? { status: 'pending' } : null,
      )
      setTotalSectionsToProcess(0)
      setTopoWarnings([])
      setSummary(null)
      setError(null)

      const ctrl = new AbortController()
      abortRef.current = ctrl

      void (async () => {
        try {
          for await (const evt of streamAllSectionsDraft(
            accessToken,
            artifactId,
            { ...(options ?? {}), signal: ctrl.signal },
          )) {
            setWaiting(aiWaitingFrom(evt))
            applyEvent(
              evt,
              setSections,
              setCurrentSection,
              setStructurePhase,
              setTotalSectionsToProcess,
              setTopoWarnings,
            )
            if (evt.type === 'orchestrator_done') {
              const p = evt.payload
              setSummary({
                total: p.total,
                succeeded: p.succeeded,
                failed: p.failed,
                skippedExisting: p.skipped_existing,
                skippedNonEditable: p.skipped_non_editable,
                aggregateInputTokens: p.aggregate_metrics.input_tokens,
                aggregateOutputTokens: p.aggregate_metrics.output_tokens,
                aggregateTotalTokens: p.aggregate_metrics.total_tokens,
                aggregateLatencyMs: p.aggregate_metrics.latency_ms,
              })
              setPhase('done')
              setCurrentSection(null)
              break
            }
            if (evt.type === 'error') {
              setError({
                ...(evt.payload.code !== undefined
                  ? { code: evt.payload.code }
                  : {}),
                message: evt.payload.message,
              })
              setPhase('error')
              setCurrentSection(null)
              break
            }
          }
        } catch (exc) {
          if (ctrl.signal.aborted) {
            setPhase('idle')
            setCurrentSection(null)
            return
          }
          // Corte de red (`STREAM_INTERRUPTED`) o error HTTP previo al
          // stream: conservar el código para mostrar la copy traducida.
          const e = exc instanceof Error ? exc : new Error(String(exc))
          setError({
            ...(exc instanceof ApiError ? { code: exc.code } : {}),
            message: e.message,
          })
          setPhase('error')
          setCurrentSection(null)
        } finally {
          // Un `start` posterior ya pudo reemplazar el controller.
          if (abortRef.current === ctrl) abortRef.current = null
          setWaiting(null)
        }
      })()
    },
    [accessToken, artifactId],
  )

  // Abort al desmontar — evita request fantasma cuando el modal cierra
  // a la mitad.
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
    }
  }, [])

  return {
    phase,
    currentSection,
    sections,
    structurePhase,
    totalSectionsToProcess,
    topoWarnings,
    summary,
    waiting,
    error,
    start,
    abort,
    reset,
  }
}

/** Aplica un evento del stream al estado React. Mantiene el Map
 * ordenado por orden de inserción (cada `section_start` agrega o
 * actualiza). */
function applyEvent(
  evt: DraftAllStreamEvent,
  setSections: React.Dispatch<
    React.SetStateAction<Map<string, SectionProgress>>
  >,
  setCurrentSection: React.Dispatch<
    React.SetStateAction<CurrentSectionInfo | null>
  >,
  setStructurePhase: React.Dispatch<
    React.SetStateAction<StructurePhaseState | null>
  >,
  setTotalSectionsToProcess: React.Dispatch<React.SetStateAction<number>>,
  setTopoWarnings: React.Dispatch<React.SetStateAction<string[]>>,
): void {
  if (evt.type === 'orchestrator_start') {
    return
  }
  if (evt.type === 'structure_phase_start') {
    setStructurePhase({ status: 'running' })
    return
  }
  if (evt.type === 'structure_phase_done') {
    setStructurePhase({
      status: 'done',
      instancesAdded: evt.payload.instances_added,
      rationale: evt.payload.rationale,
    })
    return
  }
  if (evt.type === 'structure_phase_failed') {
    setStructurePhase({
      status: 'failed',
      errorCode: evt.payload.code,
      errorMessage: evt.payload.message,
    })
    return
  }
  if (evt.type === 'topology_built') {
    setTotalSectionsToProcess(evt.payload.total)
    setTopoWarnings(evt.payload.topo_warnings)
    return
  }
  if (evt.type === 'section_skipped') {
    setSections((prev) => {
      const next = new Map(prev)
      const key = makeKey(evt.payload.section_id, evt.payload.instance_key)
      next.set(key, {
        sectionId: evt.payload.section_id,
        instanceKey: evt.payload.instance_key,
        status: 'skipped',
        skipReason: evt.payload.reason,
      })
      return next
    })
    return
  }
  if (evt.type === 'section_start') {
    const info: CurrentSectionInfo = {
      index: evt.payload.index,
      total: evt.payload.total,
      sectionId: evt.payload.section_id,
      instanceKey: evt.payload.instance_key,
      sectionType: evt.payload.section_type,
    }
    setCurrentSection(info)
    setSections((prev) => {
      const next = new Map(prev)
      const key = makeKey(evt.payload.section_id, evt.payload.instance_key)
      next.set(key, {
        sectionId: evt.payload.section_id,
        instanceKey: evt.payload.instance_key,
        status: 'running',
        sectionType: evt.payload.section_type,
      })
      return next
    })
    return
  }
  if (evt.type === 'section_done') {
    setSections((prev) => {
      const next = new Map(prev)
      const key = makeKey(evt.payload.section_id, evt.payload.instance_key)
      const existing = next.get(key)
      next.set(key, {
        sectionId: evt.payload.section_id,
        instanceKey: evt.payload.instance_key,
        status: 'done',
        ...(existing?.sectionType !== undefined
          ? { sectionType: existing.sectionType }
          : {}),
        draftChars: evt.payload.draft_chars,
        tokensInput: evt.payload.tokens_input,
        tokensOutput: evt.payload.tokens_output,
        latencyMs: evt.payload.latency_ms,
      })
      return next
    })
    return
  }
  if (evt.type === 'section_error') {
    setSections((prev) => {
      const next = new Map(prev)
      const key = makeKey(evt.payload.section_id, evt.payload.instance_key)
      const existing = next.get(key)
      next.set(key, {
        sectionId: evt.payload.section_id,
        instanceKey: evt.payload.instance_key,
        status: 'error',
        ...(existing?.sectionType !== undefined
          ? { sectionType: existing.sectionType }
          : {}),
        errorCode: evt.payload.code,
        errorMessage: evt.payload.message,
      })
      return next
    })
    return
  }
  // 'orchestrator_done' y 'error' los maneja el caller (necesita
  // setSummary y setPhase).
}
